import { ref } from 'vue'

export interface FileUploadOptions {
  maxFiles?: number
  maxFileSize?: number // in bytes
  allowedTypes?: string[] // MIME types
  multiple?: boolean
}

export const useFileUpload = (options: FileUploadOptions = {}) => {
  const {
    maxFiles = 10,
    maxFileSize = 20 * 1024 * 1024, // 20MB
    allowedTypes = [], // Empty array means allow all types
    multiple = true,
  } = options

  // State
  const files = ref<File[]>([])
  const fileInput = ref<HTMLInputElement>()
  const isDragOver = ref(false)
  const errors = ref<string[]>([])

  // Security: Sanitize filename to prevent XSS
  const sanitizeFilename = (filename: string): string => {
    // Remove HTML tags, scripts, and other dangerous characters
    return filename
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[<>:"'|?*]/g, '_') // Replace dangerous characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 255) // Limit length
  }

  // Security: Get file extension from filename (more reliable than MIME type)
  const getFileExtensionSecure = (filename: string): string => {
    const sanitized = sanitizeFilename(filename)
    return sanitized.split('.').pop()?.toLowerCase() || ''
  }

  // Security: Common dangerous file extensions
  const dangerousExtensions = [
    'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar', 'app', 'deb', 'pkg', 'dmg',
    'msi', 'run', 'elf', 'bin', 'sh', 'ps1', 'php', 'asp', 'jsp', 'war', 'ear'
  ]

  // Enhanced file validation with security checks
  const validateFile = (file: File): string | null => {
    // Security: Sanitize and validate filename
    const sanitizedName = sanitizeFilename(file.name)
    const fileExtension = getFileExtensionSecure(file.name)

    // Security: Check for dangerous file extensions
    if (dangerousExtensions.includes(fileExtension)) {
      return `File "${sanitizedName}" has a potentially dangerous extension (${fileExtension}).`
    }

    // Security: Check for suspicious filenames
    if (file.name.length > 255) {
      return `File name is too long (max 255 characters).`
    }

    // Check file size
    if (file.size > maxFileSize) {
      return `File "${sanitizedName}" is too large. Maximum size is ${formatFileSize(maxFileSize)}.`
    }

    // Security: Basic file size validation (empty files can be suspicious)
    if (file.size === 0) {
      return `File "${sanitizedName}" is empty.`
    }

    // Check file type if restrictions exist
    if (allowedTypes.length > 0) {
      const isAllowed = allowedTypes.some(allowedType => {
        // Handle wildcard patterns like "image/*"
        if (allowedType.includes('/*')) {
          const baseType = allowedType.replace('/*', '')
          return file.type.startsWith(baseType + '/')
        }

        // Handle exact MIME type matches
        return file.type === allowedType
      })

      if (!isAllowed) {
        return `File "${sanitizedName}" type (${file.type}) is not allowed. Allowed types: ${allowedTypes.join(', ')}`
      }

      // Security: Cross-check file extension with MIME type for images
      if (allowedTypes.some(type => type.startsWith('image'))) {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']
        if (!imageExtensions.includes(fileExtension)) {
          return `File "${sanitizedName}" extension (${fileExtension}) doesn't match expected image types.`
        }
      }
    }

    return null
  }

  // Add files with validation
  const addFiles = (newFiles: File[]) => {
    errors.value = []
    const filesToAdd: File[] = []

    for (const file of newFiles) {
      // Check file count limit
      if (files.value.length + filesToAdd.length > maxFiles) {
        errors.value.push(`Maximum ${maxFiles} files allowed`)
        break
      }

      // Validate file
      const error = validateFile(file)
      if (error) {
        errors.value.push(error)
        continue
      }

      // Check if file already exists
      const fileExists = files.value.some(
        (existingFile) => existingFile.name === file.name && existingFile.size === file.size
      )

      if (!fileExists && !filesToAdd.some(f => f.name === file.name && f.size === file.size)) {
        filesToAdd.push(file)
      }
    }

    // Add valid files
    files.value.push(...filesToAdd)
  }

  // Remove file by index
  const removeFile = (index: number) => {
    if (index >= 0 && index < files.value.length) {
      files.value.splice(index, 1)
      errors.value = [] // Clear errors when user removes files
    }
  }

  // Clear all files
  const clearFiles = () => {
    files.value = []
    errors.value = []
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }

  // File input handlers
  const handleFileSelect = (event: Event) => {
    const target = event.target as HTMLInputElement
    const selectedFiles = target.files
    if (selectedFiles) {
      addFiles(Array.from(selectedFiles))
    }
  }

  // Drag and drop handlers
  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    isDragOver.value = false

    if (event.dataTransfer?.files) {
      addFiles(Array.from(event.dataTransfer.files))
    }
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
  }

  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault()
    isDragOver.value = true
  }

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    const currentTarget = event.currentTarget as HTMLElement
    const relatedTarget = event.relatedTarget as Node
    if (!currentTarget?.contains(relatedTarget)) {
      isDragOver.value = false
    }
  }

  // Utility functions
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const openFileDialog = () => {
    fileInput.value?.click()
  }

  // Get file extension from filename
  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || ''
  }

  // Check if file is an image
  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/')
  }

  return {
    // State
    files,
    fileInput,
    isDragOver,
    errors,

    // Configuration
    maxFiles,
    maxFileSize,
    allowedTypes,
    multiple,

    // Methods
    addFiles,
    removeFile,
    clearFiles,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    openFileDialog,

    // Utilities
    formatFileSize,
    getFileExtension,
    isImageFile,
    validateFile,
  }
}
